import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, Shield, Lock, Eye, Database, Globe, Bell, UserCheck, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function PrivacyPolicy() {
  const lastUpdated = "29 de Março de 2026";
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <span className="font-heading font-bold text-lg">Viral Clipes</span>
          </Link>
          <Button variant="ghost" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">Política de Privacidade</h1>
          </div>
          <p className="text-muted-foreground">Última atualização: {lastUpdated}</p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          {/* Introdução */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">Introdução</h2>
            <p className="text-muted-foreground leading-relaxed">
              O Viral Clipes (&quot;nós&quot;, &quot;nosso&quot; ou &quot;Serviço&quot;) está comprometido em proteger sua privacidade. 
              Esta Política de Privacidade explica como coletamos, usamos, divulgamos e protegemos suas 
              informações quando você usa nossa plataforma.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Esta política está em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018), 
              o Marco Civil da Internet (Lei nº 12.965/2014), o Regulamento Geral sobre a Proteção de Dados (GDPR) 
              da União Europeia, e as políticas de privacidade do Google (YouTube), Meta (Instagram) e TikTok.
            </p>
          </section>

          {/* 1. Informações que Coletamos */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              1. Informações que Coletamos
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">1.1 Informações Fornecidas por Você</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Dados de Cadastro:</strong> Nome, endereço de e-mail, senha (criptografada)</li>
                  <li><strong>Dados de Perfil:</strong> Foto de perfil, preferências de idioma, configurações de conta</li>
                  <li><strong>Dados de Pagamento:</strong> Processados diretamente pelo Stripe; não armazenamos dados completos de cartão</li>
                  <li><strong>Comunicações:</strong> Mensagens de suporte, feedback e correspondências</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">1.2 Informações de Plataformas de Terceiros</h3>
                <p className="text-muted-foreground mb-2">
                  Quando você conecta contas de redes sociais, coletamos:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>YouTube:</strong> ID do canal, nome do canal, tokens de acesso OAuth, estatísticas de vídeos públicos</li>
                  <li><strong>TikTok:</strong> ID do usuário, nome de usuário, tokens de acesso, métricas de vídeos</li>
                  <li><strong>Instagram:</strong> ID da conta, nome de usuário, tokens de acesso, insights de mídia</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">1.3 Informações Coletadas Automaticamente</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Dados de Uso:</strong> Páginas visitadas, recursos utilizados, ações realizadas</li>
                  <li><strong>Dados do Dispositivo:</strong> Tipo de navegador, sistema operacional, endereço IP</li>
                  <li><strong>Cookies e Tecnologias Similares:</strong> Para autenticação, preferências e análises</li>
                  <li><strong>Logs de Segurança:</strong> Registros de login, tentativas de acesso, alterações de conta</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 2. Como Usamos Suas Informações */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              2. Como Usamos Suas Informações
            </h2>
            <p className="text-muted-foreground mb-4">
              Utilizamos as informações coletadas para:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Fornecer, manter e melhorar o Serviço</li>
              <li>Processar transações e gerenciar sua assinatura</li>
              <li>Conectar e publicar conteúdo em suas contas de redes sociais</li>
              <li>Analisar vídeos e identificar momentos virais usando IA</li>
              <li>Gerar clipes e processá-los conforme suas configurações</li>
              <li>Enviar notificações sobre atividades da conta e atualizações do Serviço</li>
              <li>Fornecer suporte ao cliente</li>
              <li>Detectar, prevenir e resolver problemas técnicos e de segurança</li>
              <li>Cumprir obrigações legais</li>
              <li>Gerar análises agregadas e anônimas para melhorar o Serviço</li>
            </ul>
          </section>

          {/* 3. Bases Legais para Processamento (LGPD/GDPR) */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary" />
              3. Bases Legais para Processamento
            </h2>
            <p className="text-muted-foreground mb-4">
              Processamos seus dados pessoais com base nas seguintes bases legais da LGPD:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-3">
              <li>
                <strong>Consentimento (Art. 7º, I):</strong> Quando você cria uma conta, conecta redes sociais 
                ou opta por receber comunicações de marketing
              </li>
              <li>
                <strong>Execução de Contrato (Art. 7º, V):</strong> Para fornecer o Serviço conforme nossos 
                Termos de Uso
              </li>
              <li>
                <strong>Legítimo Interesse (Art. 7º, IX):</strong> Para melhorar o Serviço, prevenir fraudes 
                e garantir segurança
              </li>
              <li>
                <strong>Cumprimento de Obrigação Legal (Art. 7º, II):</strong> Para cumprir leis e regulamentos aplicáveis
              </li>
            </ul>
          </section>

          {/* 4. Integração com APIs de Terceiros */}
          <section className="glass-card p-6 rounded-xl border border-primary/30">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              4. Integração com APIs de Terceiros
            </h2>
            
            <div className="space-y-6">
              <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg">
                <h3 className="font-semibold mb-2 text-red-400">4.1 API do YouTube (Google)</h3>
                <p className="text-muted-foreground mb-2">
                  O uso da API do YouTube pelo Viral Clipes está sujeito à 
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">Política de Privacidade do Google</a>.
                </p>
                <p className="text-muted-foreground mb-2">
                  <strong>Dados acessados:</strong> Informações do canal, vídeos públicos, estatísticas, capacidade de upload
                </p>
                <p className="text-muted-foreground mb-2">
                  <strong>Propósito:</strong> Buscar vídeos para análise, publicar YouTube Shorts, obter métricas de desempenho
                </p>
                <p className="text-muted-foreground">
                  <strong>Revogar acesso:</strong> Você pode revogar o acesso a qualquer momento em{' '}
                  <a href="https://security.google.com/settings/security/permissions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    security.google.com/settings/security/permissions
                  </a>
                </p>
              </div>

              <div className="bg-pink-500/10 border border-pink-500/30 p-4 rounded-lg">
                <h3 className="font-semibold mb-2 text-pink-400">4.2 API do TikTok</h3>
                <p className="text-muted-foreground mb-2">
                  O uso da API do TikTok está sujeito à 
                  <a href="https://www.tiktok.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">Política de Privacidade do TikTok</a>.
                </p>
                <p className="text-muted-foreground mb-2">
                  <strong>Dados acessados:</strong> Informações do perfil, capacidade de publicação, métricas de vídeos
                </p>
                <p className="text-muted-foreground mb-2">
                  <strong>Propósito:</strong> Publicar vídeos no TikTok, obter métricas de desempenho
                </p>
                <p className="text-muted-foreground">
                  <strong>Revogar acesso:</strong> Nas configurações do TikTok → Privacidade → Gerenciar permissões de apps
                </p>
              </div>

              <div className="bg-purple-500/10 border border-purple-500/30 p-4 rounded-lg">
                <h3 className="font-semibold mb-2 text-purple-400">4.3 API do Instagram (Meta)</h3>
                <p className="text-muted-foreground mb-2">
                  O uso da API do Instagram está sujeito à 
                  <a href="https://privacycenter.instagram.com/policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">Política de Privacidade do Instagram</a>.
                </p>
                <p className="text-muted-foreground mb-2">
                  <strong>Dados acessados:</strong> Informações da conta comercial, capacidade de publicação, insights de mídia
                </p>
                <p className="text-muted-foreground mb-2">
                  <strong>Propósito:</strong> Publicar Reels no Instagram, obter métricas de desempenho
                </p>
                <p className="text-muted-foreground">
                  <strong>Revogar acesso:</strong> Nas configurações do Instagram → Segurança → Apps e sites
                </p>
              </div>
            </div>
          </section>

          {/* 5. Compartilhamento de Informações */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">5. Compartilhamento de Informações</h2>
            <p className="text-muted-foreground mb-4">
              Não vendemos suas informações pessoais. Compartilhamos dados apenas nas seguintes circunstâncias:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-3">
              <li>
                <strong>Provedores de Serviço:</strong> Empresas que nos ajudam a operar o Serviço 
                (hospedagem, processamento de pagamentos, análises)
              </li>
              <li>
                <strong>Plataformas de Redes Sociais:</strong> YouTube, TikTok e Instagram para publicação 
                de conteúdo conforme sua autorização
              </li>
              <li>
                <strong>Requisitos Legais:</strong> Quando exigido por lei, processo judicial ou autoridades governamentais
              </li>
              <li>
                <strong>Proteção de Direitos:</strong> Para proteger nossos direitos, privacidade, segurança 
                ou propriedade
              </li>
              <li>
                <strong>Transações Comerciais:</strong> Em caso de fusão, aquisição ou venda de ativos, 
                com notificação prévia
              </li>
            </ul>
          </section>

          {/* 6. Segurança dos Dados */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              6. Segurança dos Dados
            </h2>
            <p className="text-muted-foreground mb-4">
              Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Criptografia de senhas usando Argon2id (padrão industrial)</li>
              <li>Transmissão de dados criptografada via HTTPS/TLS</li>
              <li>Tokens OAuth armazenados de forma segura e criptografada</li>
              <li>Autenticação de dois fatores (2FA) disponível</li>
              <li>Monitoramento de segurança e logs de auditoria</li>
              <li>Controle de acesso baseado em funções</li>
              <li>Backups regulares e recuperação de desastres</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Apesar de nossos esforços, nenhum método de transmissão pela Internet ou armazenamento eletrônico 
              é 100% seguro. Não podemos garantir segurança absoluta.
            </p>
          </section>

          {/* 7. Retenção de Dados */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">7. Retenção de Dados</h2>
            <p className="text-muted-foreground mb-4">
              Retemos suas informações pessoais pelo tempo necessário para:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Fornecer o Serviço enquanto sua conta estiver ativa</li>
              <li>Cumprir obrigações legais, contábeis ou fiscais</li>
              <li>Resolver disputas e fazer cumprir nossos acordos</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              <strong>Períodos de retenção:</strong>
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-2">
              <li>Dados de conta: Durante a vigência da conta + 5 anos após encerramento</li>
              <li>Logs de segurança: 2 anos</li>
              <li>Dados de pagamento: Conforme exigências fiscais (5 anos no Brasil)</li>
              <li>Comunicações de suporte: 3 anos</li>
            </ul>
          </section>

          {/* 8. Seus Direitos (LGPD) */}
          <section className="glass-card p-6 rounded-xl border border-green-500/30">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-400" />
              8. Seus Direitos (LGPD)
            </h2>
            <p className="text-muted-foreground mb-4">
              De acordo com a LGPD, você tem os seguintes direitos:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-3">
              <li>
                <strong>Confirmação e Acesso:</strong> Confirmar se processamos seus dados e acessar uma cópia
              </li>
              <li>
                <strong>Correção:</strong> Solicitar correção de dados incompletos, inexatos ou desatualizados
              </li>
              <li>
                <strong>Anonimização, Bloqueio ou Eliminação:</strong> Solicitar anonimização ou exclusão 
                de dados desnecessários ou excessivos
              </li>
              <li>
                <strong>Portabilidade:</strong> Receber seus dados em formato estruturado para transferência
              </li>
              <li>
                <strong>Eliminação:</strong> Solicitar exclusão de dados pessoais tratados com base em consentimento
              </li>
              <li>
                <strong>Informação:</strong> Ser informado sobre entidades com as quais compartilhamos dados
              </li>
              <li>
                <strong>Revogação do Consentimento:</strong> Revogar consentimento a qualquer momento
              </li>
              <li>
                <strong>Oposição:</strong> Opor-se ao tratamento que viole a LGPD
              </li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Para exercer esses direitos, entre em contato pelo e-mail: <strong>privacy@viralclipes.com</strong>
            </p>
          </section>

          {/* 9. Exclusão de Dados */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-primary" />
              9. Exclusão de Conta e Dados
            </h2>
            <p className="text-muted-foreground mb-4">
              Você pode solicitar a exclusão de sua conta e dados pessoais a qualquer momento:
            </p>
            <ol className="list-decimal list-inside text-muted-foreground space-y-2">
              <li>Acesse Configurações → Segurança → Excluir Conta</li>
              <li>Ou envie e-mail para privacy@viralclipes.com com o assunto &quot;Exclusão de Conta&quot;</li>
            </ol>
            <p className="text-muted-foreground mt-4">
              Após a solicitação, excluiremos seus dados em até 30 dias, exceto quando a retenção for 
              necessária para cumprir obrigações legais.
            </p>
          </section>

          {/* 10. Cookies */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">10. Cookies e Tecnologias de Rastreamento</h2>
            <p className="text-muted-foreground mb-4">
              Usamos cookies e tecnologias similares para:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li><strong>Cookies Essenciais:</strong> Necessários para funcionamento básico (autenticação, segurança)</li>
              <li><strong>Cookies de Preferências:</strong> Lembrar suas configurações e preferências</li>
              <li><strong>Cookies Analíticos:</strong> Entender como você usa o Serviço para melhorá-lo</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Você pode controlar cookies nas configurações do seu navegador. Desabilitar cookies essenciais 
              pode afetar a funcionalidade do Serviço.
            </p>
          </section>

          {/* 11. Transferência Internacional */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">11. Transferência Internacional de Dados</h2>
            <p className="text-muted-foreground leading-relaxed">
              Seus dados podem ser transferidos e mantidos em servidores localizados fora do Brasil, 
              incluindo Estados Unidos e União Europeia. Ao usar o Serviço, você consente com essa 
              transferência.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Garantimos que qualquer transferência internacional de dados está em conformidade com a LGPD 
              e utilizamos salvaguardas apropriadas, como cláusulas contratuais padrão.
            </p>
          </section>

          {/* 12. Menores de Idade */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">12. Privacidade de Menores</h2>
            <p className="text-muted-foreground leading-relaxed">
              O Serviço não é destinado a menores de 18 anos. Não coletamos intencionalmente informações 
              pessoais de menores. Se você é pai/mãe ou responsável e sabe que seu filho nos forneceu 
              dados pessoais, entre em contato conosco.
            </p>
          </section>

          {/* 13. Notificações */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              13. Comunicações e Notificações
            </h2>
            <p className="text-muted-foreground mb-4">
              Podemos enviar comunicações sobre:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li><strong>Transacionais:</strong> Confirmações de conta, recibos, alertas de segurança</li>
              <li><strong>Serviço:</strong> Atualizações, manutenção, novos recursos</li>
              <li><strong>Marketing:</strong> Promoções e ofertas (com seu consentimento)</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Você pode optar por não receber comunicações de marketing nas configurações da conta ou 
              clicando em &quot;cancelar inscrição&quot; nos e-mails.
            </p>
          </section>

          {/* 14. Alterações */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">14. Alterações nesta Política</h2>
            <p className="text-muted-foreground leading-relaxed">
              Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos sobre alterações 
              significativas por e-mail e/ou aviso destacado no Serviço antes da entrada em vigor.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Recomendamos revisar esta Política periodicamente. O uso continuado do Serviço após 
              alterações constitui aceitação da Política revisada.
            </p>
          </section>

          {/* 15. Contato DPO */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">15. Contato e Encarregado de Dados (DPO)</h2>
            <p className="text-muted-foreground leading-relaxed">
              Para questões sobre esta Política de Privacidade ou exercer seus direitos:
            </p>
            <div className="mt-4 p-4 bg-muted/50 rounded-lg space-y-2">
              <p className="font-medium">Viral Clipes - Privacidade de Dados</p>
              <p className="text-muted-foreground">Email geral: privacy@viralclipes.com</p>
              <p className="text-muted-foreground">Encarregado de Dados (DPO): dpo@viralclipes.com</p>
              <p className="text-muted-foreground">Website: https://viralclipes.com</p>
            </div>
            <p className="text-muted-foreground mt-4">
              Você também tem o direito de apresentar reclamação à Autoridade Nacional de Proteção de Dados (ANPD).
            </p>
          </section>

          {/* Footer Links */}
          <div className="flex flex-wrap gap-4 pt-8 border-t border-border">
            <Link to="/terms" className="text-primary hover:underline">
              Termos de Uso
            </Link>
            <Link to="/" className="text-primary hover:underline">
              Página Inicial
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-muted-foreground text-sm">
          <p>&copy; {new Date().getFullYear()} Viral Clipes. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
